import * as React from "react";
import type { Edge, Node } from "@xyflow/react";

import {
  asConfigSchema,
  type AutomationConfigField,
} from "#app/hooks/automations/use-automation-catalog";
import { at } from "#app/lib/utils/at";
import { INTEGRATION_OUTBOUND_STEP_KEY } from "#app/hooks/automations/use-automation-outbound-targets";
import type { Omittable } from "@salestrak/ui";
import type { FlowNodeData } from "#app/components/automations/canvas/nodes";

// A single dynamic value an author can drop into a templatable field, e.g.
// `trigger.issue.name` → "Issue name".
export interface VariableItem {
  path: string;
  label: string;
  type: string;
  // Display-only sample value (from the catalog schema, or generated per
  // custom-field type) — shown in the "Available variables" reference so
  // authors can see what shape the real data takes.
  example?: string | undefined;
}

// Variables grouped by their source — the trigger, or one upstream step.
export interface VariableGroup {
  id: string;
  label: string;
  items: VariableItem[];
  // Upstream-step groups only: the node's unique slug (see computeStepSlugs),
  // matching the badge shown on the canvas node — pickers surface it beside
  // the group label so authors can tie a group back to its node.
  slug?: string | undefined;
  // Sample of this group's FULL source object at run time (id fields included,
  // unlike `items`), assembled from the group's raw schema — what the webhook
  // payload preview shows for the matching source. Only on groups that map to
  // a whole payload source: upstream steps (the step's output object) and the
  // integration trigger (`{ integration: … }`, the run scope's trigger shape).
  example?: Record<string, unknown> | undefined;
}

// Catalog metadata this module reads, narrowed from the catalog hook. Both
// schemas arrive as JSON scalars (ConfigField[]).
interface TriggerMetaEntry {
  label: string;
  contextSchema: unknown;
}
interface StepMetaEntry {
  label: string;
  outputSchema: unknown;
}

// A workspace custom field, surfaced as an issue variable. Dynamic (per
// workspace), so it can't live in the code-defined trigger contextSchema.
export interface IssueCustomFieldVar {
  id: string;
  name: string;
  type: string;
  // Sample value for the variables reference (see sampleForCustomField).
  example?: string | undefined;
}

// A generated sample value per custom-field type (packages/enums FIELD/TYPES
// vocabulary) for the "Available variables" reference. Select-likes prefer the
// field's real first option so the sample reads as the workspace's own data.
const CUSTOM_FIELD_SAMPLES: Record<string, string> = {
  text: "Sample text",
  paragraph: "A longer freeform note about this project.",
  number: "42",
  money: "12500",
  date: "2026-08-15",
  time: "16:30",
  phone: "(208) 555-0142",
  email: "alex@example.com",
  url: "https://example.com",
  checkbox: "true",
  member: "Alex Rivera",
  address: "742 Evergreen Terrace, Boise, ID",
  region: "Northwest",
  formula: "1250",
  photos: "3 photos",
  files: "2 files",
};

export function sampleForCustomField(
  type: string,
  firstOptionLabel?: string | null,
): string | undefined {
  if (type === "select") return firstOptionLabel ?? undefined;
  return CUSTOM_FIELD_SAMPLES[type];
}

const TRIGGER_ID = "trigger";

// ── Variable-group ids ──────────────────────────────────────────────────────
// The group-id vocabulary consumers key on (the webhook payload Source, the
// condition builder's step subjects) is minted HERE and only here — consumers
// import these instead of restating the literals, so an id format change
// cannot silently strand a string-match in another file.
export const TRIGGER_PROJECT_GROUP_ID = "trigger:project";
export const TRIGGER_INTEGRATION_GROUP_ID = "trigger:integration";
const STEP_GROUP_PREFIX = "step-";

export function stepGroupId(nodeId: string): string {
  return `${STEP_GROUP_PREFIX}${nodeId}`;
}

// The node id a step group's id names, or null for non-step groups.
export function stepIdFromGroupId(groupId: string): string | null {
  return groupId.startsWith(STEP_GROUP_PREFIX)
    ? groupId.slice(STEP_GROUP_PREFIX.length)
    : null;
}

// Short, stable per-type identifier base for each step. Instances are numbered
// (`note-0`, `note-1`) so duplicate step types stay distinguishable on the
// canvas and in the variable picker.
const STEP_SLUGS: Record<string, string> = {
  updateIssueField: "field",
  moveIssueStage: "stage",
  setUpdateCycle: "cycle",
  assignIssue: "assign",
  // "comment": the UI never says "note" (the schema keeps the noun).
  addNote: "comment",
  createIssue: "project",
  archiveIssue: "archive",
  notify: "notify",
  sendEmail: "email",
  webhook: "webhook",
  // The connection-targeted step. Keyed off the catalog constant because the key
  // itself ("integration.outbound") is the one thing a slug must never be: it
  // reads as an internal identifier, and the dot makes it look like a path.
  [INTEGRATION_OUTBOUND_STEP_KEY]: "connection",
  branch: "branch",
  filter: "filter",
  delay: "delay",
};

// Unique, human-readable slug per step node — `<base>-<n>` where the base is
// the step type's short identifier (STEP_SLUGS) and n counts ALL steps of
// that type in canvas order, 1-based ("comment-1", "webhook-2"). Always numbered
// so a slug never changes meaning when a second same-type step appears.
// (Deleting or reordering same-type steps DOES renumber the survivors —
// acceptable only because the slug is display-only and never stored:
// variable paths keep the stable `steps.<id>.*` node ids.) The slug is the
// human handle shown on the node's hover toolbar and beside the matching
// variable group in pickers.
export function computeStepSlugs(nodes: Node[]): Map<string, string> {
  const counts = new Map<string, number>();
  const result = new Map<string, string>();
  for (const node of nodes) {
    if (node.type !== "step") continue;
    const data = node.data as FlowNodeData;
    const base = (data.stepType && STEP_SLUGS[data.stepType]) || data.stepType || "step";
    const n = (counts.get(base) ?? 0) + 1;
    counts.set(base, n);
    result.set(node.id, `${base}-${n}`);
  }
  return result;
}

// Human-readable ordinal per step, among UNNAMED steps of the SAME type —
// 1-based, but ONLY when that type appears more than once. A lone step gets
// `null` (no number). Drives the "2" in a node's "Call webhook 2" title and
// the matching picker label, so duplicate same-type steps are distinguishable
// in plain language. Steps the user renamed are already distinguishable, so
// they neither consume a number nor count toward their type's total.
export function computeStepOrdinals(nodes: Node[]): Map<string, number | null> {
  const order: { id: string; base: string }[] = [];
  const total = new Map<string, number>();
  const result = new Map<string, number | null>();
  for (const node of nodes) {
    if (node.type !== "step") continue;
    const data = node.data as FlowNodeData;
    if (data.stepName?.trim()) {
      result.set(node.id, null);
      continue;
    }
    const base = (data.stepType && STEP_SLUGS[data.stepType]) || data.stepType || "step";
    total.set(base, (total.get(base) ?? 0) + 1);
    order.push({ id: node.id, base });
  }
  const seen = new Map<string, number>();
  for (const { id, base } of order) {
    const idx = (seen.get(base) ?? 0) + 1;
    seen.set(base, idx);
    result.set(id, (total.get(base) ?? 0) > 1 ? idx : null);
  }
  return result;
}

// Walk incoming edges to collect every step upstream of `stepId`. Only these
// steps' outputs are referenceable — the backend rejects forward/self refs.
function ancestorStepIds(stepId: string, edges: Edge[]): Set<string> {
  const result = new Set<string>();
  const queue = [stepId];
  while (queue.length) {
    const current = queue.shift() as string;
    for (const e of edges) {
      if (e.target !== current) continue;
      if (e.source === TRIGGER_ID || result.has(e.source)) continue;
      result.add(e.source);
      queue.push(e.source);
    }
  }
  return result;
}

// Identifiers resolve to raw UUIDs at run time — meaningless inside a message,
// note, or email, and pure noise in the picker. We hide them so authors only
// ever insert human-readable values (names, dates, the project URL, custom
// fields). The data model still supports id refs if one is ever needed; we just
// don't surface them.
function isIdField(key: string): boolean {
  const seg = key.split(".").pop() ?? key;
  return seg === "id" || seg.endsWith("Id");
}

// ── Schema-derived example bodies (VariableGroup.example) ──────────────────

// A schema field's display `example` is a string; coerce it to the JSON value
// it represents per the field's declared type, with a typed placeholder when
// the schema carries none.
function exampleJsonValue(f: {
  type: string;
  example?: string | undefined;
}): unknown {
  switch (f.type) {
    case "number": {
      const n = Number(f.example);
      return f.example && Number.isFinite(n) ? n : 0;
    }
    case "boolean":
      return f.example ? f.example === "true" : true;
    case "json": {
      if (!f.example) return {};
      try {
        return JSON.parse(f.example) as unknown;
      } catch {
        return f.example;
      }
    }
    default:
      return f.example ?? "";
  }
}

// Assemble a sample object from a schema's dotted keys —
// `integration.topic` nests as `{ integration: { topic: … } }` — mirroring how
// the runner lays the same fields out on the run scope.
function exampleFromSchema(
  fields: { key: string; type: string; example?: string | undefined }[],
): Record<string, unknown> {
  const root: Record<string, unknown> = {};
  for (const f of fields) {
    const segments = f.key.split(".");
    let cursor = root;
    for (const seg of segments.slice(0, -1)) {
      const next = cursor[seg];
      if (next && typeof next === "object" && !Array.isArray(next)) {
        cursor = next as Record<string, unknown>;
      } else {
        const created: Record<string, unknown> = {};
        cursor[seg] = created;
        cursor = created;
      }
    }
    cursor[at(segments, segments.length - 1, "example key segment")] =
      exampleJsonValue(f);
  }
  return root;
}

// Build the variable picker's groups for the selected step: the trigger's
// context first, then each upstream step that exposes outputs (in canvas order).
/**
 * How the connection-bound groups are WORDED. Passed in rather than resolved
 * here: naming a connection needs the workspace's connection list, which is a
 * query, and this module is pure. The canvas already owns that wording (it words
 * the node cards from the same helpers), so it hands the answers down.
 *
 * Both fall back to the generic catalog label when absent, which is what the
 * groups read before the connection list lands.
 */
export type VariableGroupNaming = Omittable<{
  /** Header for a connection-sourced trigger's `integration.*` group. */
  triggerGroup?: string;
  /** Per-step header, given the node and the label it would otherwise carry. */
  stepLabel?: (node: Node, generic: string) => string;
  /**
   * EXTRA outputs a connection-targeted step's chosen action declares, on top of
   * the ones its step type does.
   *
   * A resolver rather than a value because the answer depends on the workspace's
   * outbound connections, which only exist behind a query — the same reason
   * `stepLabel` is one. Returning nothing is always safe: the step type's
   * generic outputs stand alone, which is what every step showed before any
   * action declared its own.
   */
  stepOutputs?: (node: Node) => AutomationConfigField[] | null;
}>;

/**
 * The variable TYPES a step inserted AFTER `sourceId` would be able to
 * reference — the trigger's context, every step upstream of the source, and the
 * source's own outputs.
 *
 * Powers the add-step picker, which decides what to offer BEFORE the step it
 * would create exists. Rather than restate how outputs are collected, it asks
 * the same function on behalf of an imaginary node wired downstream of the
 * source: the walk then treats the source as an ancestor, which is exactly what
 * it will be. The placeholder id is absent from `nodes`, so it contributes
 * nothing of its own.
 */
export function variableTypesAfter(
  sourceId: string,
  nodes: Node[],
  edges: Edge[],
  triggerSourceKey: string | null | undefined,
  triggerMeta: Map<string, TriggerMetaEntry>,
  stepMeta: Map<string, StepMetaEntry>,
  issueCustomFields: IssueCustomFieldVar[] = [],
  naming: VariableGroupNaming = {},
): ReadonlySet<string> {
  const placeholder = "__inserted__";
  return variableTypesIn(
    getStepVariableGroups(
      placeholder,
      nodes,
      [...edges, { id: placeholder, source: sourceId, target: placeholder }],
      triggerSourceKey,
      triggerMeta,
      stepMeta,
      issueCustomFields,
      naming,
    ),
  );
}

export function getStepVariableGroups(
  stepId: string,
  nodes: Node[],
  edges: Edge[],
  triggerSourceKey: string | null | undefined,
  triggerMeta: Map<string, TriggerMetaEntry>,
  stepMeta: Map<string, StepMetaEntry>,
  issueCustomFields: IssueCustomFieldVar[] = [],
  naming: VariableGroupNaming = {},
): VariableGroup[] {
  const groups: VariableGroup[] = [];

  const tMeta = triggerSourceKey ? triggerMeta.get(triggerSourceKey) : undefined;
  const triggerFields = asConfigSchema(tMeta?.contextSchema);
  if (triggerFields.length) {
    // Split the trigger's context into meaningful categories instead of one flat
    // list: the record's built-in properties ("Project properties"), the acting
    // user ("Triggered by"), and — below — the workspace's custom fields.
    // Classification is by key prefix; our context keys are consistent.
    const projectItems: VariableItem[] = [];
    const actorItems: VariableItem[] = [];
    // A connection-sourced trigger's context (`integration.*`) is not project
    // data — filing it under "Project properties" buried the two fields that
    // actually resolve at the bottom of a list of issue fields.
    const integrationItems: VariableItem[] = [];
    for (const f of triggerFields) {
      if (isIdField(f.key)) continue;
      const item: VariableItem = {
        path: `trigger.${f.key}`,
        label: f.label,
        type: f.type,
        example: f.example,
      };
      if (f.key.startsWith("actor.")) actorItems.push(item);
      else if (f.key.startsWith("integration.")) integrationItems.push(item);
      else projectItems.push(item);
    }
    // Workspace custom fields for issue-based triggers (those carrying `issue.*`
    // context) — they resolve off the triggering issue at run time.
    const customItems: VariableItem[] = [];
    if (triggerFields.some((f) => f.key.startsWith("issue."))) {
      for (const cf of issueCustomFields) {
        customItems.push({
          path: `trigger.issue.customFields.${cf.id}`,
          label: cf.name,
          type: cf.type,
          example: cf.example,
        });
      }
    }
    // Emit in order: built-in properties, then custom fields, then the actor.
    // Empty categories are skipped (e.g. "Triggered by" stays hidden until the
    // actor context exposes more than its id, which is filtered as an id field).
    // The integration group leads for a connection-sourced trigger — it's the
    // only context that exists there, so nothing should sit above it.
    if (integrationItems.length) {
      groups.push({
        id: TRIGGER_INTEGRATION_GROUP_ID,
        // "Connection triggered from <connection>", not the catalog's
        // "Integration event" — which is identical on every such automation and
        // names neither the system nor the event the variables came from.
        label: naming.triggerGroup ?? "Integration event",
        items: integrationItems,
        // From the RAW integration fields (ids kept): the dotted keys nest to
        // `{ integration: … }`, the trigger's shape on the run scope.
        example: exampleFromSchema(
          triggerFields.filter((f) => f.key.startsWith("integration.")),
        ),
      });
    }
    if (projectItems.length) {
      groups.push({ id: TRIGGER_PROJECT_GROUP_ID, label: "Project properties", items: projectItems });
    }
    if (customItems.length) {
      groups.push({ id: "trigger:custom", label: "Custom fields", items: customItems });
    }
    if (actorItems.length) {
      groups.push({ id: "trigger:actor", label: "Triggered by", items: actorItems });
    }
  }

  const ancestors = ancestorStepIds(stepId, edges);
  const ordinals = computeStepOrdinals(nodes);
  const slugs = computeStepSlugs(nodes);
  for (const node of nodes) {
    if (node.type !== "step" || !ancestors.has(node.id)) continue;
    const data = node.data as FlowNodeData;
    const meta = data.stepType ? stepMeta.get(data.stepType) : undefined;
    // The chosen action's outputs REPLACE the step type's when it declares any.
    // The step type's list is the fallback, exactly as it documents itself: its
    // entries describe the DISPATCH (`ok`, "Provider key", "Action key") rather
    // than the work, and they exist so a provider with no real actions is still
    // exercisable. Appending an action's real outputs to them buries the useful
    // ones in scaffolding.
    //
    // An action re-declares any generic key still worth offering. Nothing is
    // lost at run time: the step still RETURNS the full set, so an automation
    // authored before a narrowing keeps resolving.
    const actionOut = naming.stepOutputs?.(node) ?? null;
    const allOutFields = actionOut?.length ? actionOut : asConfigSchema(meta?.outputSchema);
    const outFields = allOutFields.filter((f) => !isIdField(f.key));
    if (!outFields.length) continue;
    // Human label matching the node header — the user's custom name when
    // given, else "Call webhook 2" — so the picker and the canvas node read
    // the same. The slug ties the group to the node's hover badge.
    const generic = meta?.label ?? data.label ?? "Step";
    // A connection-targeted step reads as the action and connection it performs
    // ("<action> with <connection>"); everything else keeps its catalog
    // label. Same wording the node card and the go-live preview use.
    const base = naming.stepLabel?.(node, generic) ?? generic;
    const ord = ordinals.get(node.id);
    groups.push({
      id: stepGroupId(node.id),
      label: data.stepName?.trim() || (ord ? `${base} ${ord}` : base),
      slug: slugs.get(node.id),
      items: outFields.map((f) => ({
        path: `steps.${node.id}.${f.key}`,
        label: f.label,
        type: f.type,
        example: f.example,
      })),
      // The full output object (ids kept) — the body a webhook posts when this
      // step is its payload source.
      example: exampleFromSchema(allOutFields),
    });
  }

  return groups;
}

// Drop variables whose type the target can't accept (the compatibility
// contract lives in field-capabilities.ts updateFieldVariableTypes, mirrored
// server-side). `null` = free text, where every SCALAR passes. Empty groups
// disappear, so InsertVariableMenu/VariableTextInput hide their affordance when
// nothing fits.
export function filterVariableGroupsByType(
  groups: VariableGroup[],
  allowed: ReadonlySet<string> | null,
): VariableGroup[] {
  return groups
    .map((g) => ({
      ...g,
      // Free text is "any scalar", not "anything". A structured type's value is
      // a record, so interpolating one into a sentence yields JSON — the same
      // reason ids are hidden (isIdField): a variable that can only render
      // badly is worse than one that is absent. A field that wants the record
      // names its type and gets it.
      items: g.items.filter((item) =>
        allowed === null ? !isStructuredType(item.type) : allowed.has(item.type),
      ),
    }))
    .filter((g) => g.items.length > 0);
}

// Config field types whose value is a RECORD rather than a scalar. MIRRORS
// STRUCTURED_FIELD_TYPES in the server's automations/catalog/config-schema.ts,
// which is what the publish gate refuses in a free-text target — keep the two
// in sync, the same way field-capabilities.ts mirrors variable-compat.ts.
const STRUCTURED_TYPES = new Set(["issue"]);

export function isStructuredType(type: string): boolean {
  return STRUCTURED_TYPES.has(type);
}

// Every variable TYPE reachable at a node. Feeds the action lists, which hide
// an action needing a type nothing here produces (actionFitsGraph).
export function variableTypesIn(groups: VariableGroup[]): ReadonlySet<string> {
  const types = new Set<string>();
  for (const g of groups) for (const item of g.items) types.add(item.type);
  return types;
}


// ── Variable label resolution (for rendering refs as chips) ───────────────
// The path→label mapping is position-independent (a `steps.<id>.<field>` ref
// always means the same step/field), so a single map covering the trigger +
// every step serves every node's chip rendering — no per-node recompute.
// Null = not a known variable: every renderer leaves the reference as literal
// `{{ path }}` text — nothing ever chips up that isn't a real variable.
export type VariableLabelResolver = (path: string) => string | null;

// Relative-date tokens from the date value builder (`{{now}}`, `{{now+3d}}`,
// `{{now-3d}}`) are valid template refs the runner resolves against the wall
// clock (mirrors the server's NOW_TOKEN_RE in catalog/variable-compat.ts).
// They aren't catalog paths, so no label map contains them — every resolver
// special-cases them through here so they chip up like real variables instead
// of degrading to literal mustache text. Labels mirror the relative-date
// builder's vocabulary in steps/update-field.tsx.
const NOW_TOKEN_RE = /^now(?:\s*([+-])\s*(\d+)\s*d)?$/i;

export function relativeDateLabel(path: string): string | null {
  const m = NOW_TOKEN_RE.exec(path);
  if (!m) return null;
  const days = Number(m[2] ?? 0);
  if (!m[1] || !days) return "Now (run time)";
  const unit = days === 1 ? "day" : "days";
  return m[1] === "+" ? `${days} ${unit} after now` : `${days} ${unit} before now`;
}

/**
 * path → label, for rendering a stored `{{ }}` reference as a readable chip.
 *
 * Takes the same `stepOutputs` resolver `getStepVariableGroups` does, and must:
 * a variable this map does not know renders as its raw path, so an author who
 * inserted "Sent to" would see `steps.abc123.channelLabel` on the node card. The
 * picker offering something the canvas cannot name is worse than not offering
 * it.
 */
export function buildVariableLabelMap(
  nodes: Node[],
  triggerSourceKey: string | null | undefined,
  triggerMeta: Map<string, TriggerMetaEntry>,
  stepMeta: Map<string, StepMetaEntry>,
  issueCustomFields: IssueCustomFieldVar[] = [],
  naming: VariableGroupNaming = {},
): Map<string, string> {
  const map = new Map<string, string>();
  const tMeta = triggerSourceKey ? triggerMeta.get(triggerSourceKey) : undefined;
  const triggerFields = asConfigSchema(tMeta?.contextSchema);
  for (const f of triggerFields) {
    map.set(`trigger.${f.key}`, f.label);
  }
  // Issue-based triggers expose workspace custom fields off the issue.
  if (triggerFields.some((f) => f.key.startsWith("issue."))) {
    for (const cf of issueCustomFields) {
      map.set(`trigger.issue.customFields.${cf.id}`, cf.name);
    }
  }
  for (const node of nodes) {
    if (node.type !== "step") continue;
    const data = node.data as FlowNodeData;
    const meta = data.stepType ? stepMeta.get(data.stepType) : undefined;
    // The action's list when it declares one, else the step type's — the same
    // replacement the groups builder applies, so a chip can never be named
    // differently from the row that inserted it.
    //
    // Deliberately NOT narrowed to what is currently OFFERED: a step authored
    // before an action narrowed its outputs may still reference a generic key,
    // and that reference has to keep rendering as a chip rather than reverting
    // to a raw path. So the step type's labels are also kept as a fallback layer
    // beneath the action's.
    for (const f of asConfigSchema(meta?.outputSchema)) {
      map.set(`steps.${node.id}.${f.key}`, f.label);
    }
    for (const f of naming.stepOutputs?.(node) ?? []) {
      map.set(`steps.${node.id}.${f.key}`, f.label);
    }
  }
  return map;
}

// Default resolver knows no variables (nothing renders as a chip).
const VariableLabelContext = React.createContext<VariableLabelResolver>(
  () => null,
);

export const VariableLabelProvider = VariableLabelContext.Provider;

export function useVariableLabel(): VariableLabelResolver {
  return React.useContext(VariableLabelContext);
}

// Per-step ordinal (see computeStepOrdinals) provided to node components so a
// duplicated step shows "… 2" in its header. `null` when the step is unique.
const NodeOrdinalContext = React.createContext<Map<string, number | null>>(new Map());

export const NodeOrdinalProvider = NodeOrdinalContext.Provider;

export function useNodeOrdinal(id: string): number | null {
  return React.useContext(NodeOrdinalContext).get(id) ?? null;
}

// Per-step slug (see computeStepSlugs) provided to node components for the
// hover-toolbar badge — the same slug pickers show beside the node's variable
// group. `null` for non-step nodes.
const NodeSlugContext = React.createContext<Map<string, string>>(new Map());

export const NodeSlugProvider = NodeSlugContext.Provider;

export function useNodeSlug(id: string): string | null {
  return React.useContext(NodeSlugContext).get(id) ?? null;
}

// ── Canvas orientation (left-to-right vs top-to-bottom) ────────────────────
// Provided by the canvas so node components position their handles and "add"
// affordances along the active axis. Layout positions swap axis to match.
export type CanvasOrientation = "horizontal" | "vertical";

const OrientationContext = React.createContext<CanvasOrientation>("horizontal");

export const OrientationProvider = OrientationContext.Provider;

export function useOrientation(): CanvasOrientation {
  return React.useContext(OrientationContext);
}

// The workflow this automation is scoped to (its "location"), or null for the
// workspace-level rollup. Drives workflow-scoped option pools — e.g. the stage
// picker in stage actions/conditions — so a location-pinned automation
// pre-scopes its data instead of starting empty. When null, those controls fall
// back to letting the user pick a workflow first.
const WorkflowScopeContext = React.createContext<string | null>(null);

export const WorkflowScopeProvider = WorkflowScopeContext.Provider;

export function useWorkflowScopeId(): string | null {
  return React.useContext(WorkflowScopeContext);
}

